import { Button, Typography } from '@modulor/react';
import ErrorStatusIcon from 'assets/ErrorStatusIcon';
import SuccessStatusIcon from 'assets/SuccessStatusIcon';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import { useEffect } from 'react';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { FlowTypes } from 'store/constants';
import { useAppSelector } from 'store/hooks';
import type { PayHubCashierContractsReversePendingWithdrawalV2Response } from 'store/models';
import { currencyDisplaySelector } from 'store/slices/deposit';
import { getFormattedAmountWithCurrency } from 'utils';

const clickstream = getEventAnalytic();

interface PartialReverseResultProps {
  result?: PayHubCashierContractsReversePendingWithdrawalV2Response;
  onDeposit: () => void;
  onBackToGame?: () => void;
}

export const PartialReverseResult: FC<PartialReverseResultProps> = ({ result, onDeposit, onBackToGame }) => {
  const { translate } = useSkeletonTranslation();
  const cashierContext = useGetClickStreamCashierContext(FlowTypes.withdrawal);
  const currencyDisplaySetting = useAppSelector(currencyDisplaySelector);

  useEffect(() => {
    const event = result
      ? getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_success_view
      : getClickStreamEvent.pw_homepage_popup_cancel_withdrawal_error_view;
    clickstream.push(event, [cashierContext]);
  }, []);

  const formatAmount = (amount: number, currency: string) =>
    getFormattedAmountWithCurrency(`${amount}`, currency, currencyDisplaySetting);

  const { title, text } = result
    ? {
        title: translate('PH.DEPOSIT.PARTIAL_REVERSE.RESULT.TITLE'),
        text: translate('PH.DEPOSIT.PARTIAL_REVERSE.RESULT.TEXT', {
          returned: formatAmount(result.success_amount, result.currency),
          remain: formatAmount(result.remain_amount, result.currency),
        }),
      }
    : {
        title: translate('PH.DEPOSIT.PARTIAL_REVERSE.ERROR.TITLE'),
        text: translate('PH.DEPOSIT.PARTIAL_REVERSE.ERROR.TEXT'),
      };

  return (
    <div className="partial-reverse-result" data-testid="partial-reverse-result">
      {result ? <SuccessStatusIcon /> : <ErrorStatusIcon />}
      <div className="partial-reverse-result__text">
        <Typography variant="body-semibold" tag="h1">
          {title}
        </Typography>
        <Typography variant="subhead-regular" tag="p" className="partial-reverse-result__description">
          {text}
        </Typography>
      </div>
      {result ? (
        <Button className="partial-reverse-result__button" variant="deposit" onClick={onBackToGame}>
          {translate('PH.BUTTON.BACK_TO_GAME')}
        </Button>
      ) : (
        <Button className="partial-reverse-result__button" variant="deposit" onClick={onDeposit}>
          {translate('CASHIER.DEPOSIT')}
        </Button>
      )}
    </div>
  );
};
